import {
  DashboardEvent,
  LookerEmbedExSDK,
  PagePropertiesChangedEvent,
} from '@looker/embed-sdk'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import {
  RawCookielessSessionAcquireResponse,
  useGenerateInsightsLabV2CookielessTokensMutation,
  useLazyAcquireInsightsLabV2CookielessSessionQuery,
} from 'api/insightsLabV2'
import Loading from 'components/Loading'
import { buildTranslate } from 'locales'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { errorHandler } from 'utilities/errorHandler'

const t = buildTranslate('insights_lab_v2')

type Props = {
  reportId: number
  // Used as the iframe's accessible title (WCAG 2.4.2) — the human-readable
  // report name, supplied by the workspace tab.
  reportName: string
  // Filters applied when first opening the report (e.g. a favorite's saved
  // state). Captured once for the signed embed URL — live changes inside the
  // dashboard are handled by Looker, surfaced via onFilterChange.
  initialFilters?: Record<string, string>
  onFilterChange?: (filters: Record<string, string>) => void
}

// Sanity bound for the height Looker reports via postMessage.
const MAX_FRAME_HEIGHT = 10000

// Bare Looker host (no protocol, no /api/4.0) — required by
// @looker/embed-sdk's initCookieless() to verify messages from the embed.
const LOOKER_EMBED_HOST = process.env.REACT_APP_LOOKER_EMBED_HOST ?? ''

// PagePropertiesChangedEvent's base interface (LookerEmbedEvent) declares a
// `[key: string]: any` index signature, so tsc happily typed reads of
// `event.dashboard`/`event.page` on this event as `any` even though the SDK
// only ever puts `height` on it (see the doc comment on
// PagePropertiesChangedEvent itself) — that index signature is exactly why
// the bug this file used to have (reading nonexistent fields off this event)
// was never caught at compile time. Picking just the field this handler
// actually needs strips that index signature, so any future attempt to read
// a field this event doesn't have becomes a real type error again.
type HeightChangedEvent = Pick<PagePropertiesChangedEvent, 'height'>

// Cookieless Embedding (Insights Lab v2 only — this is the only embedding path
// now that Insights Lab v2's entire feature depends on it; see Lab.tsx's
// 'insights-lab-v2' flag guard). Each report iframe acquires its own
// token-scoped session via the backend's cookieless_session/cookieless_tokens
// endpoints — no shared Looker-domain cookie, so switching merchants can't
// leak a stale session into a reopened report.
//
// Each mount gets its own `LookerEmbedExSDK` instance (constructed fresh
// below, inside the effect) rather than the module-level `getEmbedSDK()`
// singleton. @looker/embed-sdk's singleton stores `_acquireSession`/
// `_generateTokens`/`_cookielessSession` directly on that one shared object,
// and `initCookieless()` overwrites them unconditionally — so with
// Workspace.tsx keeping every visited report tab mounted, a second report tab
// calling `getEmbedSDK().initCookieless(...)` would silently redirect the
// FIRST tab's periodic token-refresh calls through the second tab's session
// closures. Constructing `new LookerEmbedExSDK()` per mount (it's a plain
// exported class, not just reachable via the singleton getter) gives each
// report its own private `_acquireSession`/`_generateTokens`/
// `_cookielessSession` state, so N mounted reports refresh N independent
// sessions.
const ReportFrame = ({
  reportId,
  reportName,
  initialFilters,
  onFilterChange,
}: Props): JSX.Element => {
  // Starts at the tab's initial filters, then kept current by
  // handleFiltersChanged below as the user changes filters live inside the
  // dashboard. A plain useState captured only the initial value forever,
  // so a recovery re-embed (embedGeneration bump, see below) rebuilt the
  // dashboard with those stale filters instead of what the user was
  // actually looking at -- and then reported that reverted state back out
  // via onFilterChange, silently overwriting the workspace's live filter
  // state (and any favorite saved afterward) with it.
  const latestFiltersRef = useRef<Record<string, string>>(initialFilters ?? {})
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [height, setHeight] = useState<string>('1450px')
  // Bumped to force the embed effect below to tear down and re-run from
  // scratch (fresh acquire, fresh SDK instance, fresh iframe) after a
  // cookieless token refresh fails — see generateOnce's catch below for why.
  const [embedGeneration, setEmbedGeneration] = useState(0)
  const [css] = useStyletron()
  const containerRef = useRef<HTMLDivElement>(null)
  const onFilterChangeRef = useRef(onFilterChange)
  // Written in an effect, not during render — a ref mutation during render
  // can leak from a render React later discards/replays.
  useLayoutEffect(() => {
    onFilterChangeRef.current = onFilterChange
  }, [onFilterChange])
  // Set once per embed generation (see the effect below) the first time a
  // token-refresh failure triggers the automatic re-embed recovery, so a
  // second stale-closure call can't queue up a redundant recovery.
  const recoveryTriggeredRef = useRef(false)

  const [acquireSession] = useLazyAcquireInsightsLabV2CookielessSessionQuery()
  const [generateTokens] = useGenerateInsightsLabV2CookielessTokensMutation()

  useEffect(() => {
    let cancelled = false
    let lastSerialized = ''
    // Snapshot the container node up front: by the time the cleanup function
    // runs, containerRef.current may already point elsewhere (or null).
    const containerEl = containerRef.current
    // The SDK's generateTokens callback only receives the refreshed
    // api/navigation tokens + the session TTL — @looker/embed-sdk's
    // EmbedClientEx never re-sends session_reference_token itself. The
    // backend's refresh endpoint requires it, so it's captured here from the
    // acquire response and threaded through every refresh ourselves.
    let sessionReferenceToken: string | null = null

    // Memoize the first acquire call for this mount: we need
    // looker_dashboard_id ourselves BEFORE calling createDashboardWithId(),
    // and initCookieless() also invokes this same function internally on
    // connect() — sharing one promise avoids acquiring two sessions for one
    // iframe.
    let acquirePromise: Promise<RawCookielessSessionAcquireResponse> | undefined

    // Guards against triggering the re-embed recovery more than once per
    // generation: the Looker Embed SDK can call the stale generateOnce
    // closure again before this generation's cleanup has actually torn the
    // old iframe/SDK instance down (e.g. more than one dashboard tile
    // requesting a token refresh around the same time), which would
    // otherwise stack up redundant setEmbedGeneration bumps.
    recoveryTriggeredRef.current = false

    const acquireOnce = () => {
      if (!acquirePromise) {
        acquirePromise = acquireSession({ reportId })
          .unwrap()
          .then(session => {
            sessionReferenceToken = session.session_reference_token
            return session
          })
      }
      return acquirePromise
    }

    const generateOnce = async (tokens: {
      navigation_token?: string | null
      api_token?: string | null
    }) => {
      try {
        if (!sessionReferenceToken) {
          throw new Error(
            'Cannot refresh Looker cookieless tokens before a session has been acquired'
          )
        }
        const response = await generateTokens({
          session_reference_token: sessionReferenceToken,
          navigation_token: tokens.navigation_token ?? undefined,
          api_token: tokens.api_token ?? undefined,
        }).unwrap()
        // Looker may rotate the reference token on refresh; keep it current
        // for the next refresh cycle.
        sessionReferenceToken = response.session_reference_token
        return response
      } catch (e) {
        // The session_reference_token our backend issued has gone stale (its
        // own expiry, or the backend 404ing a malformed/expired envelope) —
        // there's no way to recover a fresh Looker token from an
        // already-dead envelope. Left uncaught, this rejection propagates
        // into the Embed SDK's internal state machine and surfaces to the
        // user as either a raw 404 behind Looker's own unrecoverable "click
        // to resume" prompt, or an unhandled `Error: Invalid state` thrown
        // from the SDK's vendored EmbedClientEx.js. Instead, catch it here
        // and force a full, fresh re-embed of this same report (new
        // session_reference_token, new SDK instance, new iframe) via the
        // embedGeneration bump below — the effect's existing cleanup tears
        // down the old iframe, and acquirePromise/sessionReferenceToken are
        // effect-local, so the next run starts from genuinely fresh state.
        // Swallowed, not rethrown: the SDK doesn't need to see this as an
        // error state — the re-embed IS the resolution.
        errorHandler.capture(
          'Looker cookieless token refresh failed, re-embedding',
          {
            reportId,
            error: e instanceof Error ? e.message : String(e),
          }
        )
        // The SDK can call this same (stale) generateOnce closure again
        // before the re-embed effect run has actually torn it down — guard
        // against queuing up more than one recovery per generation.
        if (!recoveryTriggeredRef.current) {
          recoveryTriggeredRef.current = true
          setEmbedGeneration(g => g + 1)
        }
        // The re-embed above is about to tear this whole session down, so
        // the tokens returned here are moot — but generateOnce's return type
        // (LookerEmbedCookielessSessionData, all fields optional) still
        // needs satisfying for the SDK's in-flight await.
        return {}
      }
    }

    // Height-only: this is the SDK's `page:properties:changed` event, which
    // per its actual type (and doc comment) only ever carries `height` — see
    // the HeightChangedEvent comment above. It used to also be read for
    // filter changes, but this event never carried them; that's the bug this
    // split fixes. Filter changes are handled separately by
    // handleFiltersChanged below, via the SDK's dedicated
    // `dashboard:filters:changed` event.
    const handlePageProperties = (event: HeightChangedEvent) => {
      const reportedHeight = Number(event.height)
      if (
        Number.isFinite(reportedHeight) &&
        reportedHeight > 0 &&
        reportedHeight < MAX_FRAME_HEIGHT
      ) {
        setHeight(`${reportedHeight}px`)
      }
    }

    // event.dashboard.dashboard_filters is populated by the SDK specifically
    // for this event — both `dashboard` and `dashboard_filters` are
    // non-optional in @looker/embed-sdk's types, and there's no documented
    // caveat about partial payloads. There's no URL to fall back to here:
    // this event's type carries no `page.url` field, so a fallback would
    // require an unsafe cast this codebase doesn't allow. If the SDK ever
    // fires this event with an empty filter set, that's a legitimate "no
    // filters applied" state, not a parsing failure.
    const handleFiltersChanged = (event: DashboardEvent) => {
      const filters = event.dashboard.dashboard_filters
      const serialized = new URLSearchParams(filters).toString()
      if (serialized !== lastSerialized) {
        lastSerialized = serialized
        latestFiltersRef.current = filters
        onFilterChangeRef.current?.(filters)
      }
    }

    const embed = async () => {
      if (!containerEl) return
      try {
        const session = await acquireOnce()

        // A private SDK instance for this mount only — see the block comment
        // above this component for why this can't be the shared
        // getEmbedSDK() singleton.
        const sdk = new LookerEmbedExSDK()
        sdk.initCookieless(LOOKER_EMBED_HOST, acquireOnce, generateOnce)

        await sdk
          .createDashboardWithId(String(session.looker_dashboard_id))
          .appendTo(containerEl)
          // Reads the ref's current value at build time -- the initial
          // filters on first mount, or whatever the user last set on a
          // recovery re-embed (see latestFiltersRef above).
          .withParams(latestFiltersRef.current)
          // The SDK creates its own <iframe> inside containerEl — sizing the
          // container alone leaves that iframe at the browser's tiny default
          // size, since a plain <iframe> doesn't inherit its parent's
          // dimensions. withClassName applies to the SDK's iframe directly,
          // so it fills whatever size the (dynamically-sized) container is.
          // Styletron's css() returns space-separated atomic class names in
          // one string; withClassName takes one DOM token per argument
          // (it's forwarded to classList.add, which rejects a token
          // containing whitespace), so each atomic class must be split out.
          .withClassName(
            ...css({ width: '100%', height: '100%', border: 'none' }).split(' ')
          )
          // Sets the iframe's `sandbox` attribute via the SDK's own iframe
          // builder, before the frame is ever appended/navigated. Setting
          // `sandbox` on the DOM node after `.connect()` (as this used to do)
          // is a no-op per the HTML sandboxing spec — the flags are fixed at
          // the start of navigation, and connect() has already navigated the
          // frame by the time it resolves.
          //
          // allow-popups (+ allow-popups-to-escape-sandbox) are required for
          // Looker's own dashboard "Download" menu, which triggers the
          // PDF/CSV export via window.open() to a downloadzip URL rather
          // than an in-frame navigation. Without them the browser silently
          // blocks the popup and the download never starts.
          //
          // allow-downloads is required separately: Chrome evaluates the
          // download-permission check against the INITIATING frame's sandbox
          // flags, not just the resulting popup's -- so even a popup that
          // escapes sandboxing via allow-popups-to-escape-sandbox still gets
          // blocked ("frame initiating ... the download is sandboxed") unless
          // this iframe itself also carries allow-downloads.
          // https://www.chromestatus.com/feature/5706745674465280
          .withSandboxAttr(
            'allow-scripts',
            'allow-same-origin',
            'allow-popups',
            'allow-popups-to-escape-sandbox',
            'allow-downloads'
          )
          .on('page:properties:changed', handlePageProperties)
          .on('dashboard:filters:changed', handleFiltersChanged)
          .build()
          .connect()

        if (cancelled) return

        // The SDK creates its own <iframe> inside the container; enforce the
        // same a11y posture the plain-<iframe> path had. (sandbox is applied
        // above, via the builder, before this point.)
        const iframeEl = containerEl.querySelector('iframe')
        if (iframeEl) {
          iframeEl.title = reportName
          iframeEl.setAttribute(
            'aria-label',
            t('report_region', { reportName })
          )
          iframeEl.setAttribute('data-testid', 'InsightsLabV2ReportFrame')
        }
        setStatus('ready')
      } catch (e) {
        // Without this, a cookieless embed failure (bad host config, Looker
        // rejecting the session, etc.) was completely invisible — no console
        // output, no error tracking — same failure mode LookerIframe.tsx
        // guards against for the legacy embed path.
        errorHandler.capture('Looker cookieless embed failed', {
          reportId,
          error: e instanceof Error ? e.message : String(e),
        })
        if (!cancelled) setStatus('error')
      }
    }

    embed()
    return () => {
      cancelled = true
      if (containerEl) containerEl.innerHTML = ''
    }
    // acquireSession/generateTokens are RTK Query trigger functions (stable
    // identity); latestFiltersRef/reportName are read via a ref/at build
    // time and don't need to be effect deps — the embed intentionally only
    // rebuilds when the report itself changes, or when embedGeneration is
    // bumped by generateOnce's catch above to force a full recovery
    // re-embed (which now reads the ref's current value, not a stale one).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, embedGeneration])

  if (status === 'error') {
    return (
      <div
        role="alert"
        className="my-xl text-center"
        data-testid="InsightsLabV2ReportError"
      >
        <Text.BodyText3 color="grey70">{t('report_error')}</Text.BodyText3>
      </div>
    )
  }

  return (
    <>
      {status === 'loading' && <Loading />}
      <div
        ref={containerRef}
        data-testid="InsightsLabV2ReportContainer"
        className={`w-100 border-0 ${css({ height })}`}
      />
    </>
  )
}

export default ReportFrame
